import React, { useState, useEffect } from 'react';
import { useScrollRestore } from '../hooks/useScrollRestore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, MagnifyingGlass, Chat, ArrowLeft } from '@phosphor-icons/react';
import { shouldRenderDesktop } from '../utils/deviceDetection';

const API = process.env.REACT_APP_BACKEND_URL;

const GroupListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDesktop = shouldRenderDesktop();
  
  const [groups, setGroups] = useState(() => readInstantCache('group_list', user?.id) || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(() => !readInstantCache('group_list', user?.id));
  const groupsScrollRef = useScrollRestore('group_list_scroll', [groups.length]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchGroups();
  }, [user, navigate]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/list?user_id=${user.id}`);
      const _v = response.data || [];
      setGroups(_v);
      writeInstantCache('group_list', user?.id, _v);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = groups.filter(group =>
    group.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatLastMessage = (timestamp) => {
    if (!timestamp) return 'No messages yet';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  return (
    <div className="fixed inset-0 bg-gray-100 flex flex-col overflow-hidden">
      <div className="flex-shrink-0">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 sticky top-0 z-10">
        <div className={`${isDesktop ? 'w-full px-2' : 'max-w-4xl mx-auto'} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard')}
              className="hover:bg-green-500 p-2 rounded-full transition-colors"
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <Users size={28} weight="bold" />
            <h1 className="text-xl font-bold">Groups</h1>
          </div>
          <button
            onClick={() => navigate('/groups/create')}
            className="bg-white text-green-600 px-4 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-green-50"
          >
            <Plus size={20} weight="bold" />
            New Group
          </button>
        </div>
      </div>

      {/* Search */}
      <div className={`${isDesktop ? 'w-full px-4' : 'max-w-4xl mx-auto'} p-4`}>
        <div className="relative">
          <MagnifyingGlass
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
      </div>

      {/* Groups List */}
      <div ref={groupsScrollRef} className={`flex-1 overflow-y-auto ${isDesktop ? 'w-full' : 'max-w-4xl mx-auto px-4'} pb-6`}>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading groups...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'No groups found' : 'No groups yet'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/groups/create')}
                className="bg-green-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-green-700"
              >
                Create Your First Group
              </button>
            )}
          </div>
        ) : (
          <div className={isDesktop ? 'bg-white divide-y divide-gray-100' : 'space-y-2'}>
            {filteredGroups.map(group => (
              <div
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}`)}
                className={isDesktop
                  ? 'bg-white px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer'
                  : 'bg-white rounded-lg p-4 shadow hover:shadow-md transition cursor-pointer'}
              >
                <div className="flex items-start gap-3">
                  {/* Group Icon */}
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users size={24} className="text-green-600" weight="bold" />
                  </div>

                  {/* Group Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {group.name}
                      </h3>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0 flex items-center gap-1.5">
                        {group.unread_count > 0 && (
                          <span
                            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#25D366] text-white text-[11px] font-bold"
                            data-testid={`group-unread-badge-${group.id}`}
                          >
                            {group.unread_count > 99 ? '99+' : group.unread_count}
                          </span>
                        )}
                        {formatLastMessage(group.last_message_at)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users size={16} />
                      <span>{group.members?.length || 0} members</span>
                    </div>

                    {group.description && (
                      <p className="text-sm text-gray-500 mt-1 truncate">
                        {group.description}
                      </p>
                    )}
                  </div>

                  {/* Chat Icon */}
                  <Chat size={24} className="text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupListPage;
